import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { t, formatEur } from "@/i18n/sk";
import { getCard, resolveImg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { StockBadge } from "@/components/StockBadge";
import { CardCard } from "@/components/CardCard";
import { HoloBackground } from "@/components/HoloBackground";

export default function CardDetail() {
  const { slug } = useParams();
  const { addItem, setOpen } = useCart();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setData(null); setNotFound(false); setActiveImg(0);
    getCard(slug).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="vault-bg mx-auto max-w-3xl px-4 py-32 text-center"><h1 className="font-display text-3xl font-bold text-white">Karta nenájdená</h1><Link to="/karty" className="mt-6 inline-block text-cyan-300">← {t.nav.cards}</Link></div>;
  if (!data) return <div className="vault-bg py-40 text-center text-slate-500">Načítavam…</div>;

  const c = data.card;
  const related = data.related || [];
  const buyable = c.price_eur != null && c.availability !== "sold_out";
  const img = resolveImg(c.images?.[activeImg]);

  const handleAdd = () => {
    if (!buyable) return;
    addItem({ id: c.id, slug: c.slug, name: c.name, price_eur: c.price_eur, images: c.images, expansion_name: c.expansion_name }, 1);
    toast.success(t.actions.added, { description: c.name });
    setOpen(true);
  };

  const specs = [
    [t.product.expansion, c.expansion_name],
    [t.product.cardNumber, c.card_number],
    [t.product.rarity, c.rarity],
    [t.product.cardLanguage, c.language],
    [t.product.condition, c.condition],
  ].filter(([, v]) => v);

  return (
    <div className="vault-bg">
      <section className="relative">
        <HoloBackground />
        <div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <Link to="/karty" className="mb-8 inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"><ArrowLeft className="h-4 w-4" /> {t.nav.cards}</Link>
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <div className="holo-ring group relative mx-auto flex aspect-[3/4] max-w-md items-center justify-center overflow-hidden rounded-3xl border border-white/8 bg-vault-black/60 p-6">
                <span className="holo-sweep" />
                {img && <img src={img} alt={c.name} data-testid="card-main-image" className="h-full w-full object-contain" />}
              </div>
              {c.images?.length > 1 && (
                <div className="mt-4 flex justify-center gap-3">
                  {c.images.map((im, i) => (
                    <button key={i} onClick={() => setActiveImg(i)} className={`flex h-24 w-20 items-center justify-center overflow-hidden rounded-xl border bg-vault-black/60 p-1.5 transition-colors ${i === activeImg ? "border-cyan-glow/60" : "border-white/8 hover:border-white/20"}`}>
                      <img src={resolveImg(im)} alt="" className="h-full w-full object-contain" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <span className="overline mb-3 block">{c.expansion_name}</span>
              <h1 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl" data-testid="card-title">{c.name}</h1>
              <div className="mt-5 flex flex-wrap items-center gap-4">
                <span className="font-mono text-3xl font-black text-white" data-testid="card-price">{c.price_eur != null ? formatEur(c.price_eur) : <span className="text-lg font-semibold text-slate-300">{t.product.priceOnRequest}</span>}</span>
                <StockBadge availability={c.availability} testId="card-stock-badge" />
              </div>
              {c.description && <p className="mt-6 leading-relaxed text-slate-300">{c.description}</p>}
              {c.placeholder_image && <div className="mt-4 rounded-xl border border-white/12 bg-white/5 px-4 py-3 text-xs text-slate-300">{t.product.illustrative}</div>}

              <div className="mt-8">
                <button onClick={handleAdd} disabled={!buyable} data-testid="card-add-to-cart" className="btn-glow inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-8 py-3.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none">
                  {buyable ? t.actions.addToCart : (c.price_eur == null ? t.product.priceOnRequest : t.availability.sold_out)}
                </button>
              </div>

              {specs.length > 0 && (
                <div className="mt-8 overflow-hidden rounded-xl border border-white/8">
                  <dl className="divide-y divide-white/8 text-sm">
                    {specs.map(([k, v]) => (
                      <div key={k} className="flex justify-between px-4 py-3"><dt className="text-slate-400">{k}</dt><dd className="font-medium text-white">{v}</dd></div>
                    ))}
                  </dl>
                </div>
              )}
              <div className="mt-6 flex items-center gap-2 text-sm text-slate-400"><ShieldCheck className="h-4 w-4 text-cyan-300" /> Autentická karta</div>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-20">
              <h2 className="mb-8 font-display text-2xl font-bold text-white">{t.product.related}</h2>
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
                {related.map((rc, i) => <CardCard key={rc.id} card={rc} index={i} />)}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
